import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, fromEvent, interval } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { CoachingService, InboxMode, InboxThread, MyCoach, productLineLabel } from './coaching.service';
import {
  CoachingMessageEvent,
  CoachingMessageStatusEvent,
  CoachingSocketService,
  CoachingUnknownWhatsAppEvent
} from './coaching-socket.service';
import { isStaff } from './chat-utils';

const UNREAD_POLL_MS = 60000;
const SOUND_COOLDOWN_MS = 2500;
const MARK_READ_DEBOUNCE_MS = 400;
const UNKNOWN_REFRESH_DEBOUNCE_MS = 800;

export type CoachingViewer = 'coach' | 'user' | null;

/**
 * The one unread store for coaching. Every badge (inbox tabs, unread dock,
 * messenger FAB/strip, my-coaches cards, admin "Chats" tab) reads from here.
 */
export interface CoachingUnreadState {
  viewer: CoachingViewer;
  /** Unread client→coach messages across the signed-in coach's own threads. */
  coachTotal: number;
  /** Unread coach→user messages for the signed-in user (messenger). */
  userTotal: number;
  /** Unknown WhatsApp numbers waiting to be linked. Unit: senders (number + line), not messages. */
  unknownSenders: number;
  /** Per-thread unread counts (coach: own threads seen in a list; user: their coach threads). */
  byThread: Readonly<Record<string, number>>;
}

const EMPTY_UNREAD: CoachingUnreadState = { viewer: null, coachTotal: 0, userTotal: 0, unknownSenders: 0, byThread: {} };

/**
 * App-wide coaching realtime: Socket.IO primary, unread poll fallback, the
 * shared unread store, and a soft chime + toast when a message arrives for a
 * thread that isn't focused.
 *
 * Public API for other coaching pages (e.g. admin-coaching):
 * - `unread$` — full {@link CoachingUnreadState}; `coachUnreadTotal$`, `userUnreadTotal$`,
 *   `unknownCount$`, `threadUnread$(id)` are derived slices.
 * - `markRead(threadId)` — debounced; coach → POST read, user → re-GET thread (server marks read on GET).
 * - `ingestInboxThreads(rows)` — seed per-thread counts from a thread list response.
 * - `refreshUnread()` / `refreshUnknownCount()` — re-fetch totals from the API.
 * - `resync$` — emits after a socket reconnect; refetch whatever you show.
 * - `messages$`, `messageStatus$`, `unknownWhatsApp$` — live socket events.
 */
@Injectable({ providedIn: 'root' })
export class CoachingRealtimeService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly messageSubject = new Subject<CoachingMessageEvent>();
  private readonly unreadSubject = new BehaviorSubject<CoachingUnreadState>(EMPTY_UNREAD);
  private readonly drawerOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly coachesSubject = new BehaviorSubject<MyCoach[]>([]);
  /** Holds the last requested thread until the messenger acknowledges it (it may mount late). */
  private readonly openThreadRequest = new BehaviorSubject<string | null>(null);
  private readonly unknownSubject = new Subject<CoachingUnknownWhatsAppEvent>();
  private readonly resyncSubject = new Subject<void>();
  private readonly unknownRefresh$ = new Subject<void>();

  /** Thread currently open and visible in inbox / messenger — no toast/sound/unread bump for it. */
  private focusedThreadId: string | null = null;
  private soundMuted = false;
  private lastSoundAt = 0;
  private started = false;
  private startedForUserId: string | null = null;
  private unreadPoll?: Subscription;
  private socketSubs?: Subscription;
  private audioCtx: AudioContext | null = null;
  private readonly markReadTimers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly messages$ = this.messageSubject.asObservable();
  readonly unread$ = this.unreadSubject.asObservable();
  /** Unread coach→user messages (messenger FAB). */
  readonly userUnreadTotal$ = this.slice(s => s.userTotal);
  /** Unread user→coach messages (coach inbox badge, unread dock, admin Chats tab). */
  readonly coachUnreadTotal$ = this.slice(s => s.coachTotal);
  /** Unknown WhatsApp senders waiting. */
  readonly unknownCount$ = this.slice(s => s.unknownSenders);
  readonly drawerOpen$ = this.drawerOpenSubject.asObservable();
  readonly coaches$ = this.coachesSubject.asObservable();
  /** Messenger should open this thread id (from toast / my-coaches). Call `ackThreadRequest()` once handled. */
  readonly openThreadRequest$ = this.openThreadRequest.pipe(filter((id): id is string => !!id));
  readonly unknownWhatsApp$ = this.unknownSubject.asObservable();
  readonly messageStatus$: Observable<CoachingMessageStatusEvent>;
  /** After a socket reconnect: events may have been missed, refetch open thread + list. */
  readonly resync$ = this.resyncSubject.asObservable();

  constructor(
    private auth: AuthService,
    private socket: CoachingSocketService,
    private coaching: CoachingService,
    private toast: ToastService
  ) {
    this.messageStatus$ = this.socket.messageStatus$;

    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      if (!user) {
        this.stop();
        return;
      }
      if (this.started && this.startedForUserId !== user.id) {
        // Account switch without a full logout: drop the old socket and state.
        this.stop();
      }
      this.start();
    });

    this.unknownRefresh$.pipe(debounceTime(UNKNOWN_REFRESH_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => this.refreshUnknownCount());

    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange').pipe(takeUntil(this.destroy$)).subscribe(() => {
        const id = this.focusedThreadId;
        if (!document.hidden && id && (this.unreadSubject.value.byThread[id] || 0) > 0) {
          this.markRead(id);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get unread(): CoachingUnreadState {
    return this.unreadSubject.value;
  }

  get userUnreadTotal(): number {
    return this.unreadSubject.value.userTotal;
  }

  get drawerOpen(): boolean {
    return this.drawerOpenSubject.value;
  }

  get socketConnected(): boolean {
    return this.socket.isConnected;
  }

  get focusedThread(): string | null {
    return this.focusedThreadId;
  }

  threadUnread$(threadId: string): Observable<number> {
    return this.slice(s => s.byThread[threadId] || 0);
  }

  start(): void {
    if (this.started || !this.auth.getToken()) {
      return;
    }
    this.started = true;
    this.startedForUserId = this.auth.currentUserId;
    const staff = this.isStaffCoach();
    this.patch({ ...EMPTY_UNREAD, viewer: staff ? 'coach' : 'user' });
    this.socket.connect();

    this.socketSubs?.unsubscribe();
    this.socketSubs = new Subscription();
    this.socketSubs.add(this.socket.messages$.subscribe(event => this.onMessage(event)));
    this.socketSubs.add(this.socket.reconnected$.subscribe(() => this.onReconnected()));

    if (staff) {
      this.socketSubs.add(
        this.socket.newClients$.subscribe(() => {
          this.refreshCoachUnread();
          this.toast.showInfo('A new client was assigned to you', 'Coaching');
          this.playChime();
        })
      );
      this.socketSubs.add(
        this.socket.unknownWhatsApp$.subscribe(event => {
          this.unknownSubject.next(event);
          this.unknownRefresh$.next();
          this.toast.showInfo(
            `${event.display_number || event.phone_digits}: ${(event.content || '').slice(0, 60)}`,
            'Unknown WhatsApp number',
            6000
          );
          this.playChime();
        })
      );
      this.refreshCoachUnread();
      this.refreshUnknownCount();
    } else {
      // End users only: my-coaches messenger state (never hit admin/trainer unread APIs).
      this.refreshCoaches();
      this.refreshUserUnread();
    }

    this.unreadPoll?.unsubscribe();
    this.unreadPoll = interval(UNREAD_POLL_MS).subscribe(() => {
      if (!document.hidden) {
        this.refreshUnread();
      }
    });
  }

  stop(): void {
    this.started = false;
    this.startedForUserId = null;
    this.unreadPoll?.unsubscribe();
    this.socketSubs?.unsubscribe();
    this.socketSubs = undefined;
    this.socket.disconnect();
    this.markReadTimers.forEach(t => clearTimeout(t));
    this.markReadTimers.clear();
    this.unreadSubject.next(EMPTY_UNREAD);
    this.coachesSubject.next([]);
    this.focusedThreadId = null;
    this.openThreadRequest.next(null);
    this.drawerOpenSubject.next(false);
  }

  /** Admin or trainer — eligible for coach unread dock / staff APIs. */
  isStaffCoach(): boolean {
    return isStaff(this.auth.getRoles());
  }

  // ------------------------------------------------------------ unread store

  /** Re-fetch the totals for the current viewer from the API. */
  refreshUnread(): void {
    if (this.isStaffCoach()) {
      this.refreshCoachUnread();
    } else {
      this.refreshUserUnread();
    }
  }

  /** Unknown senders, counted as the list endpoint groups them (number + line). */
  refreshUnknownCount(): void {
    if (!this.isStaffCoach() || !this.started) {
      return;
    }
    this.coaching.listUnknownWhatsApp(this.coachApiMode()).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => this.patch({ unknownSenders: res.total ?? res.items.length }),
      error: () => { /* staff without access — ignore */ }
    });
  }

  /** The inbox already loaded the unknown list — reuse its total instead of fetching again. */
  setUnknownSenders(count: number): void {
    this.patch({ unknownSenders: Math.max(0, count) });
  }

  /** @deprecated counts are senders now; this just re-fetches. */
  bumpUnknownResolved(_delta = -1): void {
    this.unknownRefresh$.next();
  }

  /** Seed per-thread counts from an inbox/dock list response (only the coach's own threads count). */
  ingestInboxThreads(rows: InboxThread[]): void {
    const byThread = { ...this.unreadSubject.value.byThread };
    for (const row of rows) {
      if (row.is_mine) {
        byThread[row.thread_id] = row.unread_count || 0;
      }
    }
    this.patch({ byThread });
  }

  /**
   * Mark a thread read (debounced per thread). Coach: POST /read, no local
   * precondition (the server ignores it for someone else's chat). User: the
   * server marks read when the thread is fetched, so re-GET it.
   */
  markRead(threadId: string): void {
    if (!threadId) {
      return;
    }
    clearTimeout(this.markReadTimers.get(threadId));
    this.markReadTimers.set(threadId, setTimeout(() => {
      this.markReadTimers.delete(threadId);
      this.clearThreadUnread(threadId);
      if (this.isStaffCoach()) {
        this.coaching.markRead(threadId, this.coachApiMode()).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => this.refreshCoachUnread(),
          error: () => { /* badge refreshes on next poll */ }
        });
      } else {
        this.coaching.getMyCoachThread(threadId).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => this.refreshUserUnread(),
          error: () => { /* non-fatal */ }
        });
      }
    }, MARK_READ_DEBOUNCE_MS));
  }

  /** Local-only: drop a thread's unread count (e.g. the server just marked it read on GET). */
  clearThreadUnread(threadId: string): void {
    const state = this.unreadSubject.value;
    const had = state.byThread[threadId] || 0;
    const byThread = { ...state.byThread, [threadId]: 0 };
    if (state.viewer === 'user') {
      const coaches = this.coachesSubject.value.map(c =>
        c.thread_id === threadId ? { ...c, unread_count: 0 } : c
      );
      this.coachesSubject.next(coaches);
      this.patch({ byThread, userTotal: Math.max(0, state.userTotal - had) });
    } else {
      this.patch({ byThread, coachTotal: Math.max(0, state.coachTotal - had) });
    }
  }

  refreshCoaches(): void {
    if (this.isStaffCoach()) {
      return;
    }
    this.coaching.getMyCoaches().pipe(takeUntil(this.destroy$)).subscribe({
      next: coaches => this.setCoaches(coaches),
      error: () => { /* non-fatal */ }
    });
  }

  // ------------------------------------------------------- focus + drawer

  /** Thread open AND visible to the viewer. Pass null on collapse / close / back. */
  setFocusedThread(threadId: string | null): void {
    this.focusedThreadId = threadId;
  }

  /** Open the messenger panel (mobile popup / desktop dock) without choosing a thread. */
  openDrawer(): void {
    this.drawerOpenSubject.next(true);
  }

  /** Open the messenger panel on a specific coach thread. */
  openThread(threadId: string): void {
    this.drawerOpenSubject.next(true);
    this.openThreadRequest.next(threadId);
  }

  ackThreadRequest(): void {
    this.openThreadRequest.next(null);
  }

  closeDrawer(): void {
    this.drawerOpenSubject.next(false);
    this.setFocusedThread(null);
  }

  toggleDrawer(): void {
    if (this.drawerOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  setMuted(muted: boolean): void {
    this.soundMuted = muted;
  }

  // ------------------------------------------------------------- internals

  private onMessage(event: CoachingMessageEvent): void {
    this.messageSubject.next(event);

    const myId = this.auth.currentUserId;
    if (myId && event.message.sender_id === myId) {
      return;
    }
    const staff = this.isStaffCoach();
    // Coach cares about client messages, the user about coach messages.
    const fromOtherSide = staff ? event.message.from_user !== false : event.message.from_user !== true;
    if (!fromOtherSide) {
      return;
    }

    const focused = this.focusedThreadId === event.thread_id;
    if (focused && !document.hidden) {
      this.markRead(event.thread_id);
      return;
    }
    this.bumpUnread(event, staff);
    if (!focused) {
      this.notify(event);
    }
  }

  private bumpUnread(event: CoachingMessageEvent, staff: boolean): void {
    const state = this.unreadSubject.value;
    const byThread = { ...state.byThread, [event.thread_id]: (state.byThread[event.thread_id] || 0) + 1 };
    if (staff) {
      this.patch({ byThread, coachTotal: state.coachTotal + 1 });
      return;
    }
    const coaches = this.coachesSubject.value.map(c =>
      c.thread_id === event.thread_id ? { ...c, unread_count: (c.unread_count || 0) + 1 } : c
    );
    this.coachesSubject.next(coaches);
    this.patch({ byThread, userTotal: state.userTotal + 1 });
  }

  private onReconnected(): void {
    this.refreshUnread();
    if (this.isStaffCoach()) {
      this.refreshUnknownCount();
    } else {
      this.refreshCoaches();
    }
    this.resyncSubject.next();
  }

  private setCoaches(coaches: MyCoach[]): void {
    this.coachesSubject.next(coaches);
    const byThread = { ...this.unreadSubject.value.byThread };
    coaches.forEach(c => c.thread_id && (byThread[c.thread_id] = c.unread_count || 0));
    this.patch({ byThread, userTotal: coaches.reduce((sum, c) => sum + (c.unread_count || 0), 0) });
  }

  private notify(event: CoachingMessageEvent): void {
    const preview = (event.message.content || '').trim().slice(0, 80);
    const line = event.product_line ? productLineLabel(event.product_line) : 'Coaching';
    const title = event.message.from_user ? 'New client message' : `${line} coach`;
    this.toast.showInfo(preview || 'New message', title, 4000);
    this.playChime();
  }

  private refreshUserUnread(): void {
    if (this.isStaffCoach() || !this.started) {
      return;
    }
    this.coaching.getMyUnreadTotal().pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ unread }) => this.patch({ userTotal: unread }),
      error: () => { /* user may not have coaching */ }
    });
  }

  private refreshCoachUnread(): void {
    if (!this.isStaffCoach() || !this.started) {
      return;
    }
    this.coaching.getUnreadTotal(this.coachApiMode()).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ unread }) => this.patch({ coachTotal: unread }),
      error: () => { /* not authorized for this mode — ignore */ }
    });
  }

  private coachApiMode(): InboxMode {
    const roles = this.auth.getRoles().map(r => String(r).toUpperCase());
    return roles.includes('ADMIN') ? 'admin' : 'coach';
  }

  private patch(partial: Partial<CoachingUnreadState>): void {
    this.unreadSubject.next({ ...this.unreadSubject.value, ...partial });
  }

  private slice<T>(pick: (s: CoachingUnreadState) => T): Observable<T> {
    return this.unreadSubject.pipe(map(pick), distinctUntilChanged());
  }

  private playChime(): void {
    if (this.soundMuted || document.hidden) {
      return;
    }
    const now = Date.now();
    if (now - this.lastSoundAt < SOUND_COOLDOWN_MS) {
      return;
    }
    this.lastSoundAt = now;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) {
        return;
      }
      if (!this.audioCtx) {
        this.audioCtx = new Ctx();
      }
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch {
      /* autoplay / audio blocked — ignore */
    }
  }
}
